/**
 * Theme configuration for the web portal that matches the Flutter app style
 */

export const AppColors = {
  // Core brand colors from Flutter app
  lightGreen: '#5CB85C',
  lightGreenAccent: '#8BC34A',
  skyBlue: '#4A90E2',
  navyBlue: '#2E3A59',
  green: '#5FA337',
  teal: '#009688',
  coral: '#FF6F61',
  purple: '#8A4FFF',
  
  // Standard UI colors
  white: '#FFFFFF',
  black: '#000000',
  
  // Additional UI colors
  lightGrey: '#F5F5F5',
  mediumGrey: '#E0E0E0',
  darkGrey: '#666666',
  error: '#D32F2F',
  success: '#388E3C',
  warning: '#FFA000',
  
  // Status colors
  statusPending: '#FFA000',
  statusReady: '#388E3C',
  statusComplete: '#3F51B5',
};

export type ThemeVariant = 'business' | 'charity' | 'default';

export interface ThemeConfig {
  primary: string;
  secondary: string;
  tertiary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  error: string;
  success: string;
  warning: string;
  cardBackground: string;
  cardShadow: string;
  buttonPrimary: string;
  buttonSecondary: string;
  inputBackground: string;
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
  fontFamily: string;
}

export const themeConfigs: Record<ThemeVariant, ThemeConfig> = {
  business: {
    primary: AppColors.skyBlue,
    secondary: AppColors.navyBlue,
    tertiary: AppColors.teal,
    accent: AppColors.lightGreen,
    background: AppColors.white,
    surface: AppColors.lightGrey,
    text: AppColors.black,
    textSecondary: AppColors.darkGrey,
    error: AppColors.error,
    success: AppColors.success,
    warning: AppColors.warning,
    cardBackground: AppColors.white,
    cardShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    buttonPrimary: AppColors.lightGreen,
    buttonSecondary: AppColors.skyBlue,
    inputBackground: AppColors.lightGrey,
    borderRadius: {
      sm: '0.25rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px',
    },
    fontFamily: "'Open Sans', sans-serif",
  },
  charity: {
    primary: AppColors.purple,
    secondary: AppColors.coral,
    tertiary: AppColors.teal,
    accent: AppColors.lightGreenAccent,
    background: AppColors.white,
    surface: '#F8F5FF', // Light purple background
    text: AppColors.black,
    textSecondary: AppColors.darkGrey,
    error: AppColors.error,
    success: AppColors.success,
    warning: AppColors.warning,
    cardBackground: AppColors.white,
    cardShadow: '0 4px 6px -1px rgba(138, 79, 255, 0.1), 0 2px 4px -1px rgba(138, 79, 255, 0.06)',
    buttonPrimary: AppColors.purple,
    buttonSecondary: AppColors.coral,
    inputBackground: AppColors.lightGrey,
    borderRadius: {
      sm: '0.25rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px',
    },
    fontFamily: "'Open Sans', sans-serif",
  },
  default: {
    primary: AppColors.skyBlue,
    secondary: AppColors.navyBlue,
    tertiary: AppColors.teal,
    accent: AppColors.lightGreen,
    background: AppColors.white,
    surface: AppColors.lightGrey,
    text: AppColors.black,
    textSecondary: AppColors.darkGrey,
    error: AppColors.error,
    success: AppColors.success,
    warning: AppColors.warning,
    cardBackground: AppColors.white,
    cardShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    buttonPrimary: AppColors.lightGreen,
    buttonSecondary: AppColors.skyBlue,
    inputBackground: AppColors.lightGrey,
    borderRadius: {
      sm: '0.25rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px',
    },
    fontFamily: "'Open Sans', sans-serif",
  },
};

// Hook to use in components
export const useTheme = (variant: ThemeVariant = 'default'): ThemeConfig => {
  return themeConfigs[variant] || themeConfigs.default;
};

// CSS variables for global theme
export const generateCssVariables = (theme: ThemeConfig): string => {
  return `
    :root {
      --color-primary: ${theme.primary};
      --color-secondary: ${theme.secondary};
      --color-tertiary: ${theme.tertiary};
      --color-accent: ${theme.accent};
      --color-background: ${theme.background};
      --color-surface: ${theme.surface};
      --color-text: ${theme.text};
      --color-text-secondary: ${theme.textSecondary};
      --color-error: ${theme.error};
      --color-success: ${theme.success};
      --color-warning: ${theme.warning};
      --card-background: ${theme.cardBackground};
      --card-shadow: ${theme.cardShadow};
      --button-primary: ${theme.buttonPrimary};
      --button-secondary: ${theme.buttonSecondary};
      --input-background: ${theme.inputBackground};
      --border-radius-sm: ${theme.borderRadius.sm};
      --border-radius-md: ${theme.borderRadius.md};
      --border-radius-lg: ${theme.borderRadius.lg};
      --border-radius-xl: ${theme.borderRadius.xl};
      --border-radius-full: ${theme.borderRadius.full};
      --font-family: ${theme.fontFamily};
    }
  `;
};
